import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';
import { errorHandlingService } from './errorHandlingService';

// Consistent API base like openaiService
const API_BASE_URL = `${ENV.API_URL}/api/affiliate`;

const getToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('affiliateService: error getting Supabase token', e);
    return '';
  }
};

const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};

// Centralized response handling
async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-JSON */ }

  if (!response.ok) {
    const message = (data && (data.message || data.error)) || text || 'Unknown error';
    errorHandlingService.logError(new Error(message), `affiliateService [${response.status}]`);
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

// Lightweight runtime guards
const requireString = (v: any, name: string) => {
  if (!v || typeof v !== 'string') throw new Error(`${name} is required`);
};

// Types
export interface ProgramSettings {
  program_name: string;
  program_description: string;
  cookie_duration: number;
  minimum_payout: number;
  payout_methods: string[];
  commission_structures?: Array<{
    id: string;
    name: string;
    type: 'percentage' | 'fixed';
    value: number;
    product_type: string;
    is_active: boolean;
  }>;
}

// Service methods
export const affiliateService = {
  // Programs (owner/admin)
  async getPrograms(): Promise<{ programs: any[] }>{
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs`, { method: 'GET', headers });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getPrograms');
      throw e;
    }
  },

  async createProgram(payload: {
    program_name: string;
    description?: string;
    default_commission_rate: number;
    commission_type: 'percentage' | 'fixed';
    cookie_duration_days?: number;
    minimum_payout?: number;
    payment_terms?: string;
    custom_payment_terms?: string;
  }): Promise<{ program: any }>{
    requireString(payload?.program_name, 'program_name');
    requireString(payload?.commission_type, 'commission_type');
    if (typeof payload?.default_commission_rate !== 'number') throw new Error('default_commission_rate is required');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.createProgram');
      throw e;
    }
  },

  // Affiliates (owner/admin)
  async listAffiliates(programId: string, status?: string): Promise<{ affiliates: any[] }>{
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates`);
      if (status) url.searchParams.set('status', status);
      const res = await fetch(url.toString(), { method: 'GET', headers });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.listAffiliates');
      throw e;
    }
  },

  async createAffiliate(programId: string, payload: {
    email: string;
    contact_name?: string;
    company_name?: string;
    website?: string;
    notes?: string;
    custom_commission_rate?: number | null;
  }): Promise<{ affiliate: any }>{
    requireString(programId, 'programId');
    requireString(payload?.email, 'email');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.createAffiliate');
      throw e;
    }
  },

  async getAffiliateDetails(programId: string, affiliateId: string): Promise<{ affiliate: any }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}`, {
        method: 'GET',
        headers,
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getAffiliateDetails');
      throw e;
    }
  },

  async updateAffiliate(programId: string, affiliateId: string, updates: {
    contact_name?: string;
    company_name?: string;
    email?: string;
    website?: string;
    notes?: string;
    custom_commission_rate?: number | null;
  }): Promise<{ affiliate: any }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updateAffiliate');
      throw e;
    }
  },

  async updateAffiliateStatus(programId: string, affiliateId: string, status: 'pending' | 'approved' | 'rejected'): Promise<{ affiliate: any }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    requireString(status, 'status');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updateAffiliateStatus');
      throw e;
    }
  },

  async getAffiliateCommissions(programId: string, affiliateId: string): Promise<{ commissions: any[] }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}/commissions`, {
        method: 'GET',
        headers,
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getAffiliateCommissions');
      throw e;
    }
  },

  async getAffiliateTraffic(programId: string, affiliateId: string, days = 30): Promise<{ traffic: any[] }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}/traffic`);
      url.searchParams.set('days', String(days));
      const res = await fetch(url.toString(), { method: 'GET', headers });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getAffiliateTraffic');
      throw e;
    }
  },

  // Signup form (owner/admin)
  async getSignupForm(programId: string): Promise<{ form: any }>{
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/signup-form`, { method: 'GET', headers });
      if (res.status === 404) {
        return { form: null };
      }
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getSignupForm');
      throw e;
    }
  },

  async upsertSignupForm(programId: string, payload: Record<string, any>, formId?: string | null): Promise<{ form: any }>{
    requireString(programId, 'programId');
    if (!payload || typeof payload !== 'object') throw new Error('payload is required');
    try {
      const headers = await authHeaders();
      const endpoint = formId
        ? `${API_BASE_URL}/programs/${encodeURIComponent(programId)}/signup-form/${encodeURIComponent(formId)}`
        : `${API_BASE_URL}/programs/${encodeURIComponent(programId)}/signup-form`;
      const res = await fetch(endpoint, {
        method: formId ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.upsertSignupForm');
      throw e;
    }
  },

  // Program Settings
  async getProgramSettings(programId: string): Promise<ProgramSettings> {
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/settings`, { method: 'GET', headers });
      return handleResponse<ProgramSettings>(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getProgramSettings');
      throw e;
    }
  },
  async updateProgramSettings(programId: string, updates: Partial<ProgramSettings>): Promise<{ success?: boolean }> {
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updateProgramSettings');
      throw e;
    }
  },
  async login(email: string, password: string): Promise<{ affiliateUserId: string; user: any; }>{
    requireString(email, 'email');
    requireString(password, 'password');

    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ email, password })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.login');
      throw e;
    }
  },

  async verifySession(affiliateUserId: string): Promise<{ valid: boolean }>{
    requireString(affiliateUserId, 'affiliateUserId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/portal/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ affiliateUserId })
      });
      const data: any = await handleResponse(res);
      return { valid: !!(data?.valid ?? data?.isValid) };
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.verifySession');
      throw e;
    }
  },

  async getProfile(affiliateUserId: string): Promise<{ profile: any; affiliate?: any }>{
    requireString(affiliateUserId, 'affiliateUserId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getProfile');
      throw e;
    }
  },

  async updateProfile(affiliateUserId: string, profile: any): Promise<{ success: boolean }>{
    requireString(affiliateUserId, 'affiliateUserId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId, profile })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updateProfile');
      throw e;
    }
  },

  async updatePaymentDetails(affiliateUserId: string, paymentDetails: any): Promise<{ success: boolean }>{
    requireString(affiliateUserId, 'affiliateUserId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/update-payment-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId, paymentDetails })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updatePaymentDetails');
      throw e;
    }
  },

  async changePassword(affiliateUserId: string, currentPassword: string, newPassword: string): Promise<{ success: boolean }>{
    requireString(affiliateUserId, 'affiliateUserId');
    requireString(currentPassword, 'currentPassword');
    requireString(newPassword, 'newPassword');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId, currentPassword, newPassword })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.changePassword');
      throw e;
    }
  },

  async getPerformance(programId: string, _affiliateUserId: string, period: string): Promise<any>{
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const url = `${API_BASE_URL}/programs/${encodeURIComponent(programId)}/analytics/performance?period=${encodeURIComponent(period)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getPerformance');
      throw e;
    }
  }
  ,

  async getProgramPerformanceMetrics(programId: string, period: '7days' | '30days' | '90days' | '12months' | 'all' = '30days'): Promise<any>{
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/analytics/performance`);
      url.searchParams.set('period', period);
      const res = await fetch(url.toString(), { method: 'GET', headers });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getProgramPerformanceMetrics');
      throw e;
    }
  }
  ,

  async getTopAffiliates(programId: string, limit = 5): Promise<{ affiliates: any[] }>{
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/analytics/top-affiliates`);
      url.searchParams.set('limit', String(limit));
      const res = await fetch(url.toString(), { method: 'GET', headers });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getTopAffiliates');
      throw e;
    }
  }
  ,

  // Admin: Payouts (PayPal initial integration)
  async listPayouts(programId: string): Promise<{ payouts: any[] }>{
    requireString(programId, 'programId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/payouts`, {
        method: 'GET',
        headers
      });
      const data: any = await handleResponse(res);
      const payouts = Array.isArray(data) ? data : (Array.isArray(data?.payouts) ? data.payouts : []);
      return { payouts };
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.listPayouts');
      throw e;
    }
  }
  ,
  async createPayout(programId: string, payout: { affiliate_id: string; amount: number; currency: string; notes?: string; payment_method?: 'paypal'; }): Promise<{ id: string }>{
    requireString(programId, 'programId');
    requireString(payout?.affiliate_id, 'payout.affiliate_id');
    if (typeof payout?.amount !== 'number') throw new Error('payout.amount is required');
    if (!payout?.currency) throw new Error('payout.currency is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(payout)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.createPayout');
      throw e;
    }
  }
  ,
  async updatePayoutStatus(programId: string, payoutId: string, status: 'pending' | 'processing' | 'paid' | 'failed'): Promise<any>{
    requireString(programId, 'programId');
    requireString(payoutId, 'payoutId');
    requireString(status, 'status');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/payouts/${encodeURIComponent(payoutId)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ status })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updatePayoutStatus');
      throw e;
    }
  }
  ,
  async processPayouts(programId: string, payoutIds: string[], status: 'pending' | 'processing' | 'paid' | 'cancelled' = 'processing'): Promise<any>{
    requireString(programId, 'programId');
    if (!Array.isArray(payoutIds) || payoutIds.length === 0) throw new Error('payoutIds is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/payouts/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ payout_ids: payoutIds, status })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.processPayouts');
      throw e;
    }
  }
  ,
  async linkPayPalPayoutMethod(programId: string, affiliateId: string, paypalEmail: string): Promise<{ success: boolean }>{
    requireString(programId, 'programId');
    requireString(affiliateId, 'affiliateId');
    requireString(paypalEmail, 'paypalEmail');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/programs/${encodeURIComponent(programId)}/affiliates/${encodeURIComponent(affiliateId)}/link-paypal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ paypal_email: paypalEmail })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.linkPayPalPayoutMethod');
      throw e;
    }
  }
  ,
  async exportPayoutsCsv(programId: string, status: 'pending' | 'processing' = 'processing'): Promise<Blob>{
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const url = `${API_BASE_URL}/programs/${encodeURIComponent(programId)}/payouts/export/csv?status=${encodeURIComponent(status)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      if (!res.ok) {
        const text = await res.text();
        try {
          const data = text ? JSON.parse(text) : null;
          const message = (data && (data.message || data.error)) || text || 'Unknown error';
          errorHandlingService.logError(new Error(message), `affiliateService.exportPayoutsCsv [${res.status}]`);
          throw new Error(message);
        } catch {
          errorHandlingService.logError(new Error(text || 'Failed to export CSV'), 'affiliateService.exportPayoutsCsv');
          throw new Error(text || 'Failed to export CSV');
        }
      }
      return await res.blob();
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.exportPayoutsCsv');
      throw e;
    }
  }
  ,

  // Admin: Commission Plans (align with backend routes)
  async getPlans(programId: string): Promise<any[]> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const url = `${API_BASE_URL}/plans?program_id=${encodeURIComponent(programId)}`;
      const res = await fetch(url, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse<any[]>(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getPlans');
      throw e;
    }
  }
  ,
  async createPlan(params: { program_id: string; name: string; commission_type: 'percentage' | 'fixed'; default_rate: number; tiers_enabled?: boolean; }): Promise<any>{
    requireString(params?.program_id, 'program_id');
    requireString(params?.name, 'name');
    requireString(params?.commission_type, 'commission_type');
    if (typeof params?.default_rate !== 'number') throw new Error('default_rate is required');
    if (!['percentage','fixed'].includes(params.commission_type)) throw new Error('commission_type must be percentage or fixed');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.createPlan');
      throw e;
    }
  }
  ,
  async updatePlan(planId: string, updates: Partial<{ name: string; commission_type: 'percentage' | 'fixed'; default_rate: number; tiers_enabled: boolean; is_active: boolean; }>): Promise<any>{
    requireString(planId, 'planId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/plans/${encodeURIComponent(planId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(updates || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.updatePlan');
      throw e;
    }
  }
  ,
  async deletePlan(planId: string): Promise<{ success: boolean }>{
    requireString(planId, 'planId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/plans/${encodeURIComponent(planId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.deletePlan');
      throw e;
    }
  }
  ,

  // Admin: Partner Tiers (controller supports upsert tiers by plan)
  async upsertPlanTiers(planId: string, tiers: Array<{ min_amount: number; rate: number }>): Promise<{ success: boolean }>{
    requireString(planId, 'planId');
    if (!Array.isArray(tiers)) throw new Error('tiers is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/plans/${encodeURIComponent(planId)}/tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ tiers })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.upsertPlanTiers');
      throw e;
    }
  }
  ,

  // Admin: Approvals
  async getApprovals(programId: string, status: 'pending' | 'approved' | 'rejected' = 'pending'): Promise<any[]> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/approvals`);
      url.searchParams.set('program_id', programId);
      url.searchParams.set('status', status);
      const res = await fetch(url.toString(), { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse<any[]>(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getApprovals');
      throw e;
    }
  }
  ,
  async setApprovalStatus(affiliateId: string, status: 'pending' | 'approved' | 'rejected', reviewer_id?: string, notes?: string): Promise<{ success: boolean }>{
    requireString(affiliateId, 'affiliateId');
    requireString(status, 'status');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/approvals/${encodeURIComponent(affiliateId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ status, reviewer_id: reviewer_id || undefined, notes: notes || undefined })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.setApprovalStatus');
      throw e;
    }
  }
  ,

  async submitTaxForm(affiliateUserId: string, formType: string, payload: Record<string, any>): Promise<{ success: boolean; status: string }>{
    requireString(affiliateUserId, 'affiliateUserId');
    requireString(formType, 'formType');
    if (!payload || typeof payload !== 'object') throw new Error('payload is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/tax/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId, form_type: formType, payload })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.submitTaxForm');
      throw e;
    }
  }
  ,

  async getTaxStatus(affiliateUserId: string): Promise<{ has_submission: boolean; latest: any }>{
    requireString(affiliateUserId, 'affiliateUserId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/portal/tax/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ affiliateUserId })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getTaxStatus');
      throw e;
    }
  }
  ,

  // Premium analytics (admin)
  async getTimeseries(range: string, programId?: string): Promise<any>{
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/analytics/performance/timeseries`);
      if (programId) url.searchParams.set('program_id', programId);
      if (range) url.searchParams.set('range', range);
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getTimeseries');
      throw e;
    }
  }
  ,

  async getSourceBreakdown(range: string, programId?: string, limit = 10): Promise<any>{
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/analytics/sources/breakdown`);
      if (programId) url.searchParams.set('program_id', programId);
      if (range) url.searchParams.set('range', range);
      url.searchParams.set('limit', String(limit));
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.getSourceBreakdown');
      throw e;
    }
  }
  ,

  async exportConversions(range: string, programId?: string): Promise<any>{
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/analytics/export/conversions`);
      if (programId) url.searchParams.set('program_id', programId);
      if (range) url.searchParams.set('range', range);
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'affiliateService.exportConversions');
      throw e;
    }
  }
};

export type AffiliateProfile = {
  contact_name: string;
  company_name?: string;
  email: string;
  phone?: string;
  website?: string;
  payment_method?: string;
  payment_email?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_routing_number?: string;
  tax_id?: string;
};
